import { useState, useMemo, useEffect, useCallback, useRef } from "react";
import { BasketItem } from "../../../../models/BasketItem";
import {
  BasketSurveyItem,
  Survey2AssortSaver,
  SurveyAnswersList,
  validateRequiredAnswers,
} from "../../../../models/Survey";
import { GET_SURVEY_URL, SAVE_SURVEY_ANSWERS_URL } from "../../../../api/apiRoutes";
import useApiRequest from "../../../../../utils/hooks/useApiRequest";
import useSilentApiRequest from "../../../../../utils/hooks/useSilentApiRequest";
import useFacility from "../../../../../utils/hooks/useFacility";
import type { ApiResult } from "../../../../models/ApiResult";
import { normalizeSurveyMapFromApi } from "../utils/normalizeSurveyMapFromApi";
import { isSurveySaveFailure } from "../utils/isSurveySaveFailure";
import { buildSurveyAnswersEntry } from "../utils/surveyAnswersPayload";
import {
  buildSurveyDraftFingerprint,
  clearSurveyDraft,
  loadSurveyDraft,
  saveSurveyDraft,
} from "../utils/surveyDraftStorage";
import type { SurveyDraft } from "../utils/surveyDraftStorage";

type SurveyFetchStatus = "idle" | "loading" | "success" | "error";

export interface UseBasketSurveyOptions {
  surveyStepActive: boolean;
}

const buildSurveyItems = (basket: BasketItem[], surveyMap: Record<string, Survey2AssortSaver>): BasketSurveyItem[] => {
  const items: BasketSurveyItem[] = [];
  basket.forEach((basketItem, lineIndex) => {
    if (!basketItem.hasSurvey) return;
    const survey = surveyMap[basketItem.assortIds];
    if (!survey) return;
    for (let i = 0; i < basketItem.qnt; i++) {
      items.push({
        assortName: basketItem.assortName,
        assortIds: basketItem.assortIds,
        validFrom: basketItem.validFrom ?? null,
        price: basketItem.price,
        basketLineIndex: lineIndex,
        instanceIndex: i,
        survey,
      });
    }
  });
  return items;
};

export const useBasketSurvey = (basket: BasketItem[], options: UseBasketSurveyOptions) => {
  const { surveyStepActive } = options;
  const { get } = useApiRequest();
  const { post: postSilent } = useSilentApiRequest();
  const facilityIds = useFacility();

  const [surveyMap, setSurveyMap] = useState<Record<string, Survey2AssortSaver>>({});
  const [fetchStatus, setFetchStatus] = useState<SurveyFetchStatus>("idle");
  const [isSaving, setIsSaving] = useState(false);
  const [surveySaveError, setSurveySaveError] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);

  const hasSurvey = basket.some((item) => item.hasSurvey);
  const draftFingerprint = useMemo(() => buildSurveyDraftFingerprint(basket), [basket]);

  useEffect(() => {
    if (!surveyStepActive || !hasSurvey) {
      setFetchStatus("idle");
      setSurveyMap({});
      return;
    }

    let cancelled = false;
    setFetchStatus("loading");
    setSurveyMap({});

    const fetchSurveys = async () => {
      const { result, isError } = await get(GET_SURVEY_URL(facilityIds));
      if (cancelled) return;
      if (!isError && Array.isArray(result)) {
        setSurveyMap(normalizeSurveyMapFromApi(result));
        setFetchStatus("success");
      } else {
        setSurveyMap({});
        setFetchStatus("error");
      }
    };

    fetchSurveys();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [surveyStepActive, hasSurvey, facilityIds]);

  const surveyItems = useMemo(() => buildSurveyItems(basket, surveyMap), [basket, surveyMap]);

  const surveyLoadError = fetchStatus === "error" || (fetchStatus === "success" && surveyItems.length === 0);

  const [currentIndex, setCurrentIndex] = useState(0);
  const [allAnswers, setAllAnswers] = useState<Record<string, string>[]>(() => surveyItems.map(() => ({})));
  const [invalidQuestionIds, setInvalidQuestionIds] = useState<Set<string>>(new Set());
  const [firstInvalidQuestionId, setFirstInvalidQuestionId] = useState<string | null>(null);
  const [surveyValidationFailSeq, setSurveyValidationFailSeq] = useState(0);
  const isHydratingRef = useRef(false);

  const surveyItemsLength = surveyItems.length;

  // Ankieta jest w pełni gotowa do pokazania dopiero gdy pobraliśmy jej definicję (fetchStatus)
  // ORAZ zakończyliśmy próbę odtworzenia zapisanego draftu (isHydrated) — patrz efekt niżej.
  const isLoadingSurvey =
    surveyStepActive &&
    hasSurvey &&
    (fetchStatus === "idle" || fetchStatus === "loading" || (surveyItemsLength > 0 && !isHydrated));

  useEffect(() => {
    // Nie czyść draftu gdy koszyk jest jeszcze pusty (pierwszy render po powrocie z innej strony).
    // Wtedy getBasket() jeszcze nie zdążył załadować danych i hasSurvey === false.
    if (basket.length > 0 && (!hasSurvey || !draftFingerprint)) {
      clearSurveyDraft(facilityIds);
    }
  }, [hasSurvey, draftFingerprint, facilityIds, basket.length]);

  useEffect(() => {
    if (surveyItemsLength === 0) {
      // Wyjście z ankiety / pusty koszyk — przy kolejnym wejściu trzeba znów spróbować odtworzyć draft.
      setIsHydrated(false);
      return;
    }
    // Poczekaj aż pobieranie definicji ankiet się zakończy — dopóki fetchStatus nie jest "success",
    // surveyItemsLength może być chwilowo niepoprawne (np. tuż po ponownym zamontowaniu strony
    // po powrocie z "Dodaj uczestnika", zanim koszyk i mapa ankiet w pełni się ustabilizują).
    if (fetchStatus !== "success") return;

    isHydratingRef.current = true;

    // Draft uznajemy za "na pewno pasujący", jeśli liczba zapisanych odpowiedzi zgadza się
    // z aktualną liczbą pozycji ankiety. Fingerprint (assortIds:qnt) traktujemy tylko jako
    // dodatkową, informacyjną wskazówkę — NIE odrzucamy na jego podstawie draftu, bo w praktyce
    // różne, kolejne odczyty koszyka z backendu potrafiły dawać drobne różnice w danych
    // niezwiązane z faktyczną zmianą zawartości koszyka.
    const isDraftUsable = (d: SurveyDraft | null): d is SurveyDraft =>
      !!d && d.allAnswers.length === surveyItemsLength;

    const draft = loadSurveyDraft(facilityIds);

    if (draft && draft.fingerprint !== draftFingerprint) {
      // eslint-disable-next-line no-console
      console.debug("[SurveyDraft] fingerprint zapisanego draftu różni się od aktualnego — kontynuuję mimo to.", {
        saved: draft.fingerprint,
        current: draftFingerprint,
      });
    }

    const applyDraft = (draftToApply: SurveyDraft | null) => {
      if (isDraftUsable(draftToApply)) {
        setCurrentIndex(Math.min(draftToApply.currentIndex, surveyItemsLength - 1));
        setAllAnswers(draftToApply.allAnswers);
      } else {
        setCurrentIndex(0);
        setAllAnswers(Array.from({ length: surveyItemsLength }, () => ({})));
        if (draftToApply) {
          clearSurveyDraft(facilityIds);
        }
      }
      setIsHydrated(true);
    };

    // Brak draftu do ochrony albo długość już się zgadza — można zastosować od razu,
    // bez dodatkowego opóźnienia (normalny, pierwszy raz wchodzenia w ankietę).
    if (!draft || isDraftUsable(draft)) {
      applyDraft(draft);
      return;
    }

    // Długość zapisanego draftu nie zgadza się z aktualnie policzoną liczbą pozycji ankiety.
    // Zamiast od razu bezpowrotnie kasować zapisane odpowiedzi, odczekaj chwilę i sprawdź
    // ponownie — to może być tylko przejściowy stan tuż po odświeżeniu koszyka.
    const timeoutId = setTimeout(() => {
      applyDraft(loadSurveyDraft(facilityIds));
    }, 400);

    return () => clearTimeout(timeoutId);
  }, [surveyItemsLength, draftFingerprint, facilityIds, fetchStatus]);

  useEffect(() => {
    if (isHydratingRef.current) {
      isHydratingRef.current = false;
      return;
    }
    if (!surveyStepActive || surveyItemsLength === 0 || !draftFingerprint || !isHydrated) {
      return;
    }

    saveSurveyDraft(facilityIds, {
      fingerprint: draftFingerprint,
      currentIndex,
      allAnswers,
    });
  }, [allAnswers, currentIndex, surveyStepActive, surveyItemsLength, draftFingerprint, facilityIds, isHydrated]);

  const setAnswer = (itemIndex: number, qId: string, value: string) => {
    setSurveySaveError(false);
    setAllAnswers((prev) => {
      const updated = [...prev];
      updated[itemIndex] = { ...updated[itemIndex], [qId]: value };
      return updated;
    });
    setInvalidQuestionIds((prev) => {
      if (!prev.has(qId)) return prev;
      const next = new Set(prev);
      next.delete(qId);
      return next;
    });
  };

  const validate = (itemIndex: number): boolean => {
    const item = surveyItems[itemIndex];
    if (!item) return true;
    const answers = allAnswers[itemIndex] || {};
    const { valid, invalidIds, firstInvalidId } = validateRequiredAnswers(item.survey, answers);

    setInvalidQuestionIds(invalidIds);
    if (valid) {
      setFirstInvalidQuestionId(null);
      return true;
    }
    setFirstInvalidQuestionId(firstInvalidId);
    setSurveyValidationFailSeq((s) => s + 1);
    return false;
  };

  const goNext = (onFinish: () => void): boolean => {
    if (!validate(currentIndex)) return false;
    if (currentIndex < surveyItems.length - 1) {
      setCurrentIndex((prev) => prev + 1);
      setInvalidQuestionIds(new Set());
    } else {
      onFinish();
    }
    return true;
  };

  const goBack = (onBasket: () => void) => {
    setSurveySaveError(false);
    setFirstInvalidQuestionId(null);
    if (currentIndex > 0) {
      setCurrentIndex((prev) => prev - 1);
      setInvalidQuestionIds(new Set());
    } else {
      onBasket();
    }
  };

  const saveSurveyAnswers = useCallback(async (): Promise<boolean> => {
    setSurveySaveError(false);
    setIsSaving(true);

    try {
      const surveyList = surveyItems.map((surveyItem, itemIdx) => {
        const line = basket[surveyItem.basketLineIndex];
        const itemAnswers = allAnswers[itemIdx] || {};
        return buildSurveyAnswersEntry(surveyItem, line, itemAnswers);
      });

      const body: SurveyAnswersList = {
        surveyList,
        facilityIds,
      };

      const response = (await postSilent(SAVE_SURVEY_ANSWERS_URL, body)) as ApiResult | undefined;

      if (isSurveySaveFailure(response)) {
        setSurveySaveError(true);
        return false;
      }

      clearSurveyDraft(facilityIds);
      return true;
    } catch {
      setSurveySaveError(true);
      return false;
    } finally {
      setIsSaving(false);
    }
  }, [basket, surveyItems, allAnswers, facilityIds, postSilent]);

  const currentItem = surveyItems[currentIndex] ?? null;
  const currentAnswers = allAnswers[currentIndex] ?? {};

  return {
    hasSurvey,
    isLoadingSurvey,
    surveyLoadError,
    isSaving,
    surveySaveError,
    surveyItems,
    currentIndex,
    currentItem,
    currentAnswers,
    setAnswer,
    goNext,
    goBack,
    invalidQuestionIds,
    firstInvalidQuestionId,
    surveyValidationFailSeq,
    saveSurveyAnswers,
  };
};
